import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminSidebar from "./Admin/AdminSidebar";
import TopNavbar, { TOPBAR_HEIGHT } from "./Layout/TopNavbar";
import EmployeeSidebar from "./Employee/EmployeeSidebar";
import EmployeeDashboard from "./Employee/EmployeeDashboard";
import ManageEmployees from "./Admin/ManageEmployees";
import RolePermissions from "./Admin/RolePermissions";
import CreateMailAccount from "./Admin/CreateMailAccount";
import EmailPage from "./Email/EmailPage";
import HardwareMaster from "./Assets/HardwareMaster";
import HardwareDestroyed from "./Assets/HardwareDestroyed";
import HardwareEmployee from "./Assets/HardwareEmployee";
import SoftwareMaster from "./Assets/SoftwareMaster";
import LeaveApplication from "./Leave/LeaveApplication";
import LeaveManagement from "./Leave/LeaveManagement";

import MessengerSidebar from "./Messanger/MessengerSidebar";
import ChatContainer from "./Messanger/ChatContainer";
import ProjectCommunication from "./Messanger/ProjectCommunication";
import { useAuthStore } from "./store/useAuthStore";
import { useChatStore } from "./store/useChatStore";
import { connectSocket } from "./socket";
import NotesPage from "./Notes/NotesPage";
import NoteEditorPage from "./Notes/NoteEditorPage";
import HRDashboard from "./HR/HRDashboard";
import JobOpenings from "./HR/JobOpenings";
import Candidates from "./HR/Candidates";
import CandidateDetail from "./HR/CandidateDetail";
import Interviews from "./HR/Interviews";
import Offers from "./HR/Offers";
import Joining from "./HR/Joining";
import ClosedHiring from "./HR/ClosedHiring";
import RecruitmentReports from "./HR/RecruitmentReports";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "http://localhost:5003"
    : window.location.origin);

/* Temporary page for modules that are not built yet */
function ComingSoon({ title }) {
  return (
    <div style={{ padding: "48px 40px", fontFamily: "Segoe UI, Arial, sans-serif" }}>
      <h2 style={{ margin: "0 0 8px", color: "#0f766e", fontSize: 26 }}>{title}</h2>
      <p style={{ margin: 0, color: "#667085", fontSize: 15 }}>
        This page is under construction.
      </p>
    </div>
  );
}

/* Messenger page: contacts on the left, chat on the right
   topOffset = height of the top bar (0 for admin layout) */
function MessengerPage({ topOffset = 0 }) {
  const selectedUser = useChatStore((s) => s.selectedUser);

  return (
    <div
      style={{
        display: "flex",
        height: `calc(100vh - ${topOffset}px)`,
        width: "100%"
      }}
    >
      <div style={{ width: 360, minWidth: 320, height: "100%" }}>
        <MessengerSidebar />
      </div>

      <div style={{ flex: 1, minWidth: 0, height: "100%" }}>
        {selectedUser ? (
          <ChatContainer />
        ) : (
          <div
            style={{
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#64748b",
              fontSize: 16
            }}
          >
            Select a contact to start chatting
          </div>
        )}
      </div>
    </div>
  );
}

/* Pages shared by Admin and Employee layouts */
function AppRoutes({ user, logout, isAdmin }) {
  return (
    <Routes>
      {/* Default */}
      <Route path="/" element={<Navigate to="/admin" replace />} />

      {/* Dashboard: Admin = old dashboard, others = Employee Dashboard */}
      <Route
        path="/admin"
        element={
          isAdmin ? (
            <Dashboard user={user} onLogout={logout} />
          ) : (
            <EmployeeDashboard user={user} />
          )
        }
      />

      {/* Notes */}
      <Route path="/tools/notes" element={<NotesPage />} />
      <Route path="/tools/notes/:noteId" element={<NoteEditorPage />} />

      {/* Email (webmail inside the ERP): /email/inbox, /email/sent ... */}
      <Route path="/email" element={<Navigate to="/email/inbox" replace />} />
      <Route path="/email/*" element={<EmailPage />} />

      {/* Manage Employees + Role & Permission */}
      <Route path="/manage-employees" element={<ManageEmployees />} />
      <Route path="/role-permissions" element={<RolePermissions />} />

   
         {/* HR: Create Mail Account (softvolute.in only, Admin only) */}
  <Route path="/hr/create-mail-account" element={<CreateMailAccount />} />

      {/* HR: Timesheet / Employee Log / Review (pages coming soon) */}
      <Route path="/timesheet" element={<ComingSoon title="Timesheet" />} />
      <Route path="/employee-log" element={<ComingSoon title="Employee Log Detail" />} />
      <Route path="/employee-reviews" element={<ComingSoon title="Employee Review" />} />

      {/* Leave: employee applies, approver reviews */}
      <Route path="/leave-application" element={<LeaveApplication />} />
      <Route path="/leave-management" element={<LeaveManagement />} />

      {/* Assets: Software / Hardware */}
      <Route path="/software" element={<SoftwareMaster />} />
      <Route path="/hardware" element={<HardwareMaster />} />
      <Route path="/hardware-destroyed" element={<HardwareDestroyed />} />
      <Route path="/my-hardware" element={<HardwareEmployee />} />

      {/* HR / Hiring Management */}
      <Route path="/hr-dashboard" element={<HRDashboard />} />
      <Route path="/hr/job-openings" element={<JobOpenings />} />
      <Route path="/hr/candidates" element={<Candidates />} />
      <Route path="/hr/candidates/:candidateId" element={<CandidateDetail />} />
      <Route path="/hr/interviews" element={<Interviews />} />
      <Route path="/hr/offers" element={<Offers />} />
      <Route path="/hr/joining" element={<Joining />} />
      <Route path="/hr/closed-hiring" element={<ClosedHiring />} />
      <Route path="/hr/recruitment-reports" element={<RecruitmentReports />} />

      {/* Messenger - User Message */}
      <Route
        path="/messenger"
        element={<MessengerPage topOffset={isAdmin ? 0 : TOPBAR_HEIGHT} />}
      />

      {/* Messenger - Project Communication */}
      <Route
        path="/messenger/project-communication"
        element={<ProjectCommunication />}
      />

      {/* Unknown Route */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const logout = () => {
    sessionStorage.removeItem("softvolute_token");
    localStorage.removeItem("token");
    useAuthStore.getState().setAuthUser(null);
    setUser(null);
  };

  const handleLogin = (u) => {
    // keep the chat stores in sync with this login
    useAuthStore.getState().setAuthUser(u);
    if (u?._id) connectSocket(u._id);
    setUser(u);
  };

  useEffect(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("softvolute_token");

    if (!token) {
      setChecking(false);
      return;
    }

    const controller = new AbortController();

    fetch(`${API_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      signal: controller.signal
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error("Session expired");
        }
        return res.json();
      })
      .then((data) => {
        localStorage.setItem("token", token);
        useAuthStore.getState().setAuthUser(data.user);
        if (data.user?._id) connectSocket(data.user._id);
        setUser(data.user);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          sessionStorage.removeItem("softvolute_token");
          localStorage.removeItem("token");
          setUser(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setChecking(false);
        }
      });

    return () => controller.abort();
  }, []);

  if (checking) {
    return (
      <div
        style={{
          height: "100vh",
          display: "grid",
          placeItems: "center",
          color: "#343a76",
          fontWeight: 600
        }}
      >
        Loading Softvolute ERP...
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const isAdmin = String(user.role || "").trim().toLowerCase() === "admin";

  /* ================= ADMIN LAYOUT (old sidebar + old dashboard) ================= */
  if (isAdmin) {
    return (
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          width: "100%",
          background: "#f4f6fb"
        }}
      >
        {/* Admin Sidebar */}
        <div
          style={{
            width: 280,
            minWidth: 280,
            height: "100vh",
            position: "sticky",
            top: 0,
            overflow: "hidden",
            background: "#2f3470"
          }}
        >
          <AdminSidebar user={user} onLogout={logout} />
        </div>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: "100vh",
            overflowX: "hidden"
          }}
        >
          <AppRoutes user={user} logout={logout} isAdmin />
        </main>
      </div>
    );
  }

  /* ================= EMPLOYEE LAYOUT (top bar + employee sidebar) ================= */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
        background: "#f4f6fb"
      }}
    >
      {/* Top bar */}
      <TopNavbar user={user} onLogout={logout} />

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Employee Sidebar */}
        <div
          style={{
            width: 250,
            minWidth: 250,
            height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            position: "sticky",
            top: TOPBAR_HEIGHT,
            overflowY: "auto",
            background: "#ffffff"
          }}
        >
          <EmployeeSidebar />
        </div>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            overflowX: "hidden"
          }}
        >
          <AppRoutes user={user} logout={logout} isAdmin={false} />
        </main>
      </div>
    </div>
  );
}

export default App;